import { useState, useEffect } from 'react';
import { Code2, Mail, X, Sparkles, Rocket, Monitor, Smartphone } from 'lucide-react';

export const DevAdBadge = () => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [isDismissed, setIsDismissed] = useState(false);
  const [isAnimating, setIsAnimating] = useState(true);

  // Animation plays once on load, then stops after 2 seconds
  useEffect(() => {
    const timeout = setTimeout(() => {
      setIsAnimating(false);
    }, 2000); // Animation runs for 2 seconds then stops
    
    return () => clearTimeout(timeout);
  }, []);

  if (isDismissed) return null;

  return (
    <div className="fixed top-20 left-2 md:top-auto md:bottom-6 md:left-4 z-[9999]">
      {/* Collapsed State - Eye-catching floating badge */}
      {!isExpanded ? (
        <button
          onClick={() => setIsExpanded(true)}
          className={`group relative flex items-center gap-1.5 md:gap-3 bg-gradient-to-r from-violet-600 via-fuchsia-600 to-pink-600 text-white pl-2 pr-3 py-1.5 md:pl-3 md:pr-5 md:py-3 rounded-xl md:rounded-2xl shadow-2xl hover:shadow-fuchsia-500/50 hover:scale-110 transition-all duration-300 scale-[0.6] md:scale-100 origin-top-left ${isAnimating ? 'animate-bounce' : ''}`}
          data-testid="dev-ad-badge"
        >
          {/* Glowing ring effect */}
          <div className="absolute inset-0 rounded-xl md:rounded-2xl bg-gradient-to-r from-violet-600 via-fuchsia-600 to-pink-600 blur-lg opacity-50 group-hover:opacity-75 transition-opacity" />
          
          {/* Icon container */}
          <div className="relative h-6 w-6 md:h-10 md:w-10 rounded-lg md:rounded-xl bg-white/20 flex items-center justify-center">
            <Rocket className="h-3 w-3 md:h-5 md:w-5 text-white" />
          </div>
          
          <div className="relative flex flex-col items-start">
            <span className="text-xs md:text-sm font-bold tracking-wide">Need a Website?</span>
            <span className="text-[8px] md:text-[10px] text-white/80">Click to learn more!</span>
          </div>
          
          <Sparkles className="relative h-3 w-3 md:h-4 md:w-4 text-yellow-300 animate-pulse" />
        </button>
      ) : (
        /* Expanded State - Premium card with details */
        <div className="relative bg-gradient-to-br from-gray-900 via-violet-950 to-gray-900 border-2 border-fuchsia-500/50 rounded-2xl md:rounded-3xl p-4 md:p-6 shadow-2xl shadow-fuchsia-500/30 w-64 md:w-80 max-w-[calc(100vw-1rem)] animate-fade-in scale-[0.75] md:scale-100 origin-top-left">
          {/* Decorative gradient orbs */}
          <div className="absolute top-0 right-0 w-24 md:w-32 h-24 md:h-32 bg-fuchsia-500/20 rounded-full blur-3xl" />
          <div className="absolute bottom-0 left-0 w-16 md:w-24 h-16 md:h-24 bg-violet-500/20 rounded-full blur-3xl" />
          
          {/* Close button */}
          <button
            onClick={() => setIsExpanded(false)}
            className="absolute top-2 right-2 md:top-4 md:right-4 text-gray-400 hover:text-white transition-colors h-6 w-6 md:h-8 md:w-8 rounded-full bg-white/10 flex items-center justify-center hover:bg-white/20"
          >
            <X className="h-3 w-3 md:h-4 md:w-4" />
          </button>

          {/* Header */}
          <div className="relative flex items-center gap-3 md:gap-4 mb-3 md:mb-4">
            <div className="h-10 w-10 md:h-14 md:w-14 rounded-xl md:rounded-2xl bg-gradient-to-br from-violet-500 via-fuchsia-500 to-pink-500 flex items-center justify-center shadow-lg shadow-fuchsia-500/30">
              <Code2 className="h-5 w-5 md:h-7 md:w-7 text-white" />
            </div>
            <div>
              <p className="text-white font-bold text-sm md:text-lg">Build Your Dream Website</p>
              <p className="text-fuchsia-400 text-xs md:text-sm font-medium">Professional Web Developer</p>
            </div>
          </div>

          {/* Message */}
          <p className="relative text-gray-300 text-xs md:text-sm leading-relaxed mb-3 md:mb-4">
            ✨ Loved this website? <span className="text-fuchsia-400 font-semibold">I built it!</span> Let me create something amazing for you too.
          </p>

          {/* Features */}
          <div className="relative flex flex-wrap gap-1.5 md:gap-2 mb-4 md:mb-5">
            <span className="flex items-center gap-1 text-[9px] md:text-[11px] bg-violet-500/20 text-violet-300 px-1.5 md:px-2 py-0.5 md:py-1 rounded-full border border-violet-500/30">
              <Monitor className="h-2.5 w-2.5 md:h-3 md:w-3" /> Responsive
            </span>
            <span className="flex items-center gap-1 text-[9px] md:text-[11px] bg-fuchsia-500/20 text-fuchsia-300 px-1.5 md:px-2 py-0.5 md:py-1 rounded-full border border-fuchsia-500/30">
              <Smartphone className="h-2.5 w-2.5 md:h-3 md:w-3" /> Mobile-First
            </span>
            <span className="flex items-center gap-1 text-[9px] md:text-[11px] bg-pink-500/20 text-pink-300 px-1.5 md:px-2 py-0.5 md:py-1 rounded-full border border-pink-500/30">
              <Rocket className="h-2.5 w-2.5 md:h-3 md:w-3" /> Fast
            </span>
          </div>

          {/* CTA */}
          <a
            href="mailto:sultan1mustafa905@gmail.com?subject=🚀 Website Development Inquiry&body=Hello Sultan,%0D%0A%0D%0AI saw your work on the AKIT website and I'm impressed! I'd like to discuss building a website.%0D%0A%0D%0AProject type: %0D%0AKey features needed: %0D%0ABudget range: %0D%0A%0D%0ALooking forward to hearing from you!"
            className="relative flex items-center justify-center gap-2 w-full bg-gradient-to-r from-violet-500 via-fuchsia-500 to-pink-500 text-white py-2.5 md:py-3.5 rounded-lg md:rounded-xl font-bold text-xs md:text-sm hover:from-violet-600 hover:via-fuchsia-600 hover:to-pink-600 transition-all shadow-lg shadow-fuchsia-500/30 hover:shadow-fuchsia-500/50 hover:scale-[1.02]"
            data-testid="dev-ad-contact"
          >
            <Mail className="h-4 w-4 md:h-5 md:w-5" />
            Let's Build Together!
          </a>

          {/* Email display */}
          <p className="relative text-center text-fuchsia-400/70 text-[10px] md:text-xs mt-2 md:mt-3">
            sultan1mustafa905@gmail.com
          </p>

          {/* Dismiss option */}
          <button
            onClick={() => setIsDismissed(true)}
            className="relative w-full text-center text-gray-500 text-[8px] md:text-[10px] mt-2 md:mt-3 hover:text-gray-400 transition-colors"
          >
            Don't show again
          </button>
        </div>
      )}
    </div>
  );
};

// More prominent version for the Advertise page
export const DevAdSection = () => {
  return (
    <div className="relative overflow-hidden bg-gradient-to-br from-emerald-900/40 via-teal-900/30 to-gray-900/50 border border-emerald-500/20 rounded-2xl p-8 my-8">
      {/* Background decoration */}
      <div className="absolute top-0 right-0 w-64 h-64 bg-emerald-500/10 rounded-full blur-3xl" />
      <div className="absolute bottom-0 left-0 w-48 h-48 bg-teal-500/10 rounded-full blur-3xl" />
      
      <div className="relative z-10">
        <div className="flex flex-col md:flex-row items-center gap-6">
          {/* Icon */}
          <div className="h-20 w-20 rounded-2xl bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center flex-shrink-0 shadow-lg shadow-emerald-500/30">
            <Code2 className="h-10 w-10 text-white" />
          </div>

          {/* Content */}
          <div className="flex-1 text-center md:text-left">
            <div className="flex items-center justify-center md:justify-start gap-2 mb-2">
              <h3 className="text-2xl font-heading font-bold text-white">
                Need a Website Like This?
              </h3>
              <Sparkles className="h-5 w-5 text-emerald-400" />
            </div>
            <p className="text-gray-300 mb-4 max-w-xl">
              This website was crafted with care and modern technologies. Whether you need a business website, 
              portfolio, e-commerce platform, or custom web application — I can build it for you!
            </p>
            
            {/* Features */}
            <div className="flex flex-wrap gap-2 mb-4 justify-center md:justify-start">
              {['Modern Design', 'Mobile Responsive', 'Fast Loading', 'SEO Optimized', 'Custom Features'].map((feature) => (
                <span 
                  key={feature}
                  className="text-xs bg-emerald-500/20 text-emerald-300 px-3 py-1 rounded-full border border-emerald-500/30"
                >
                  {feature}
                </span>
              ))}
            </div>
          </div>

          {/* CTA */}
          <div className="flex-shrink-0">
            <a
              href="mailto:sultan1mustafa905@gmail.com?subject=Website Development Inquiry&body=Hello Sultan,%0D%0A%0D%0AI am interested in having a website built. Here are my requirements:%0D%0A%0D%0A- Website type: %0D%0A- Key features needed: %0D%0A- Timeline: %0D%0A- Budget range: %0D%0A%0D%0APlease let me know your availability.%0D%0A%0D%0AThank you!"
              className="inline-flex items-center gap-2 bg-gradient-to-r from-emerald-500 to-teal-600 text-white px-8 py-4 rounded-xl font-bold text-lg hover:from-emerald-600 hover:to-teal-700 transition-all shadow-lg shadow-emerald-500/30 hover:shadow-emerald-500/50 hover:scale-105"
              data-testid="dev-ad-section-contact"
            >
              <Mail className="h-5 w-5" />
              Build Me My Website Too!
            </a>
            <p className="text-center text-emerald-400/70 text-xs mt-2">
              sultan1mustafa905@gmail.com
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};
